import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Button, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Paper,
  Grid,
  Box
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { regionForCountry } from '../../lib/findBobUtils';
import { injectIntl, FormattedMessage } from 'react-intl';

const useStyles = makeStyles((theme) => ({
  searchSidebar: {
    padding: theme.spacing(2),
    border: '1px solid rgba(0, 0, 0, 0.12)',
    backgroundColor: '#fff',
    borderRadius: 4,
  },
  horizontalLayout: {
    backgroundColor: '#002642',
    color: '#333', 
    padding: theme.spacing(3, 2),
    '& .MuiFormControl-root': {
      margin: theme.spacing(1, 0),
    },
    '& .MuiInputBase-root': {
      backgroundColor: 'white',
      borderRadius: 4,
      color: '#333',
    },
    '& .MuiSelect-select': {
      paddingTop: 10,
      paddingBottom: 10,
    },
    '& .MuiInputLabel-root': {
      color: 'white',  // Updated to white
      opacity: 1,
      position: 'relative',
      transform: 'none',
      marginBottom: theme.spacing(1),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '& .MuiFormLabel-filled': {
      color: 'white',  // Updated to white
      opacity: 1,
    },
    '& .MuiTypography-root': {  // Added to make all Typography white in horizontal mode
      color: 'white',
    },
  },
  verticalLayout: {
    '& $filterRow': {
      flexDirection: 'column',
    },
    '& $filterGrid': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  filterRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing(2),
    width: '100%',
  },
  rangeContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    '& .MuiFormControl-root': {
      flex: 1,
    },
  },
  searchBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: 'white',
    '& .MuiInputBase-root': {
      borderRadius: theme.spacing(1),  
      color: '#333',
    },
    '& .MuiInputBase-input': {
      color: '#333',
      borderRadius: theme.spacing(1), 
    },
    '& .MuiOutlinedInput-root': { 
      borderRadius: theme.spacing(1),
    },
  },
  inputLabel: {
    marginBottom: theme.spacing(1),
    color: 'inherit',
    display: 'block',
    position: 'static',
  },
  textLabel: {
    marginBottom: 0,
    color: 'inherit',
    display: 'block',
    position: 'static',
  },
  resetButton: {
    backgroundColor: 'white',
    color: '#002642',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  searchButton: {
    backgroundColor: '#f5f5f5',
    color: '#002642',
    '&:hover': {
      backgroundColor: 'white',
    },
  },
  keywordsField: {
    flex: '2 1 auto',
  },
  sectionLabel: {
    color: 'white',
    marginBottom: theme.spacing(1),
  },
  filterSection: {
    marginBottom: theme.spacing(3),
  },
  toggleButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  advancedSearchHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  conversationHistory: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxHeight: '300px',
    overflowY: 'auto',
    padding: theme.spacing(1),
    backgroundColor: '#f8f9fa',
    borderRadius: theme.spacing(1),
  },
  conversationItem: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: theme.spacing(1),
    '& p': {
      color: 'black',
    },
    '&.user': {
      backgroundColor: '#e3f2fd',
      color: 'black',
      marginLeft: '20%',
      marginRight: theme.spacing(1),
      textAlign: 'right',
      '& p': {
        color: 'black',
      },
      '& strong': {
          color: 'black',
      },
    },
    '&.ai': {
      backgroundColor: '#ffffff',
      color: 'black',
      marginRight: '20%',
      marginLeft: theme.spacing(1),
      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      textAlign: 'left',
      '& p': {
        color: 'black',
        '& strong': {
          color: 'black',
        },
      },
      
    },
  },
  timestamp: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  loadingIndicator: {
    marginLeft: '20%',
    color: 'black',
    marginRight: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: '#e3f2fd',
    borderRadius: theme.spacing(1),
    '& .dots': {
      display: 'inline-block',
      '&::after': {
        display: 'inline-block',
        color: 'black',
        content: '"..."',
        animation: '$dotsAnimation 1.5s steps(4, end) infinite',
        width: '1em',
      }
    },
    '& strong': {
      color: 'black',
    }
  },
  '@keyframes dotsAnimation': {
    '0%': { content: '"."' },
    '33%': { content: '".."' },
    '66%': { content: '"..."' },
    '100%': { content: '"..."' },
  },
}));

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const SearchSidebar = ({ 
  layout = 'vertical',
  searchInputValue,
  setSearchInputValue,
  searchParams,
  handleSearchChange,
  handleSearchSubmit,
  handleKeyPress,
  data,
  setSearchParams,
  defaultSearchParams,
  conversationHistory = [],
  resetConversation,
  isLoading = false,
  refetch,
  intl
}) => {
  const classes = useStyles();
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const [keywordsValue, setKeywordsValue] = React.useState('');
  const conversationRef = React.useRef(null);
  const allowAISearch = data?.company?.features?.includes('ai_search') || data?.getCurrentUser?.features?.includes('ai_search') || false;
  const [localSearchParams, setLocalSearchParams] = useState(searchParams);

  React.useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [conversationHistory]);

  React.useEffect(() => {
    if (data && !allowAISearch) {
      setShowAdvanced(true);
    }
  }, [data, allowAISearch]);

  React.useEffect(() => {
    setLocalSearchParams(searchParams);
  }, [searchParams]);

  React.useEffect(() => {
    // Set keywordsValue from searchParams.tags when searchParams change
    setKeywordsValue(searchParams.tags || '');
  }, [searchParams]);

  const toggleAdvanced = () => {
    setShowAdvanced(!showAdvanced);
  };

  const isHorizontal = layout === 'horizontal';

  const handleLocalSearchChange = (fieldName, value) => {
    setLocalSearchParams((prevParams) => ({
      ...prevParams,
      [fieldName]: value,
    }));
  };

  const handleSearchButtonClick = () => {
    // Directly update the parent component's searchParams
    // This is what triggers the GraphQL query
    
    // Create sanitized parameters to avoid GraphQL type errors
    const sanitized = {...localSearchParams};
    
    // Sanitize numeric fields to prevent "Could not coerce value "" to Float" errors
    const numericFields = [
      'annualRevenueGt', 'annualRevenueLt', 
      'grossProductionGt', 'grossProductionLt',
      'assetsUnderManagementGt', 'assetsUnderManagementLt',
      'yearsInPracticeGt', 'yearsInPracticeLt',
      'numberOfClientsGt', 'numberOfClientsLt',
      'recurringRevenueGt', 'recurringRevenueLt',
      'commissionRevenueGt', 'commissionRevenueLt',
      'feeBasedRevenueGt', 'feeBasedRevenueLt',
      'trailerRevenueGt', 'trailerRevenueLt',
      'directRevenueGt', 'directRevenueLt'
    ];
    
    // Remove empty fields and sanitize numeric fields
    const cleanedParams = {};
    
    Object.entries(sanitized).forEach(([key, value]) => {
      // Skip empty values
      if (value === '' || value === null || value === undefined) {
        return;
      }
      
      // Handle arrays (make sure they're not empty)
      if (Array.isArray(value) && value.length === 0) {
        return;
      }
      
      // Convert empty strings to null for numeric fields
      if (numericFields.includes(key) && value === '') {
        return; // Skip this field
      }
      
      // Keep valid values
      cleanedParams[key] = value;
    });
    
    // Add the keywords value to the tags field if not empty
    if (keywordsValue && typeof keywordsValue === 'string' && keywordsValue.trim() !== '') {
      cleanedParams.tags = keywordsValue;
    }
    
    console.log('Search Now clicked - updating params:', cleanedParams);
    
    // Update parent's searchParams directly
    setSearchParams(cleanedParams);
    
    // Explicitly refetch the query to force a network request
    if (refetch) {
      console.log('Refetching data...');
      refetch({
        ...cleanedParams,
        page: 1,
        perPage: 21
      }).then(() => {
        console.log('Refetch completed');
      }).catch(error => {
        console.error('Error refetching:', error);
      });
    }
    
    // If we're in AI search mode, we should still call handleSearchSubmit
    if (!showAdvanced) {
      handleSearchSubmit();
    }
  };

  const handleResetFilters = () => {
    setLocalSearchParams(defaultSearchParams);
    setSearchParams(defaultSearchParams);
    handleSearchSubmit();
  };

  const featureEnabled = (data, feature_name) => {
    return data?.company?.features?.includes(feature_name) || data?.getCurrentUser?.features?.includes(feature_name)
  }

  // Helper function to render a select input with same styling for both layouts
  const renderSelectField = (label, fieldName, options, multiple = false, placeholder = "Select...") => {
    const isBooleanField = ['isStudent', 'ampTraineeStatus', 'hasPartialSales', 'discretionaryInvestmentModels', 'isLimitedUser'].includes(fieldName);
    
    return (
      <FormControl fullWidth variant={isHorizontal ? "outlined" : "standard"} size={isHorizontal ? "small" : "medium"} margin={isHorizontal ? "none" : "normal"}>
        <Typography className={classes.inputLabel}>
          {intl.formatMessage({ id: `search_sidebar.headings.${label.toLowerCase().replace(/ /g, '_').replace(/\//g, '_')}` })}
        </Typography>
        <Select
          multiple={multiple}
          value={multiple ? (localSearchParams[fieldName] || []) : (localSearchParams[fieldName] ?? '')}
          onChange={(e) => {
            const value = e.target.value;
            // Convert empty string to null for boolean fields
            const processedValue = value === '' ? null : (isBooleanField ? Boolean(value) : value);
            handleLocalSearchChange(fieldName, processedValue);
          }}
          displayEmpty
        >
          <MenuItem value="" disabled><em>{placeholder}</em></MenuItem>
          {options.map((option) => {
            const value = typeof option === 'object' ? option.id : option;
            const label = typeof option === 'object' ? option.name : option;
            return (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  };

  // Helper function to render a text field with same styling for both layouts
  const renderTextField = (label, fieldName, type = "text", placeholder = "") => {
    const handleChange = (e) => {
      let value = e.target.value;
      if (type === "number") {
        // Convert to number if not empty, otherwise set to null
        value = value === '' ? null : 
               isNaN(Number(value)) ? null : 
               // Convert to string for locationWithin, keep as number for other fields
               fieldName === "locationWithin" ? String(Number(value)) :
               Number(value);
      }
      handleLocalSearchChange(fieldName, value);
    };

    return (
      <FormControl fullWidth margin={isHorizontal ? "none" : "normal"}>
        <Typography className={classes.textLabel}>
          {intl.formatMessage({ id: `search_sidebar.headings.${label.toLowerCase().replace(/ /g, '_')}` })}
        </Typography>
        <TextField
          fullWidth
          variant={isHorizontal ? "outlined" : "standard"}
          size={isHorizontal ? "small" : "medium"}
          type={type}
          placeholder={placeholder}
          value={localSearchParams[fieldName] || ''}
          onChange={handleChange}
          inputProps={{
            min: type === "number" ? 0 : undefined
          }}
        />
      </FormControl>
    );
  };

  const transitionGoals = data?.getCurrentUser?.searchable_transition_goals || [];

  // Create a proper component for range fields (now inside the main component)
  const RangeFieldComponent = ({ title, minField, maxField, options }) => {
    const [minValue, setMinValue] = useState(localSearchParams[minField] || '');
    const [maxValue, setMaxValue] = useState(localSearchParams[maxField] || '');

    // Update state when props change
    React.useEffect(() => {
      setMinValue(localSearchParams[minField] || '');
      setMaxValue(localSearchParams[maxField] || '');
    }, [localSearchParams, minField, maxField]);

    const handleRangeChange = (fieldName, value) => {
      const numValue = value === '' ? null : 
                      isNaN(Number(value)) ? null : 
                      Number(value);
      if (fieldName === minField) {
        setMinValue(value);
      } else if (fieldName === maxField) {
        setMaxValue(value);
      }
      handleLocalSearchChange(fieldName, numValue);
    };

    return (
      <div>
        <Typography className={isHorizontal ? classes.sectionLabel : ""}>
          {intl.formatMessage({ id: `search_sidebar.headings.${title.toLowerCase().replace(/ /g, '_')}` })}
        </Typography>
        <Box className={classes.rangeContainer}>
          <FormControl variant={isHorizontal ? "outlined" : "standard"} size={isHorizontal ? "small" : "medium"}>
            <Typography className={classes.inputLabel}>
              {intl.formatMessage({ id: 'search_sidebar.min' })}
            </Typography>
            <Select
              value={minValue}
              onChange={(e) => handleRangeChange(minField, e.target.value)}
              displayEmpty
            >
              <MenuItem value=""><em>{intl.formatMessage({ id: 'search_sidebar.min' })}</em></MenuItem>
              {options.min.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant={isHorizontal ? "outlined" : "standard"} size={isHorizontal ? "small" : "medium"}>
            <Typography className={classes.inputLabel}>
              {intl.formatMessage({ id: 'search_sidebar.max' })}
            </Typography>
            <Select
              value={maxValue}
              onChange={(e) => handleRangeChange(maxField, e.target.value)}
              displayEmpty
            >
              <MenuItem value=""><em>{intl.formatMessage({ id: 'search_sidebar.max' })}</em></MenuItem>
              {options.max.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </div>
    );
  };

  // Helper function to render range inputs - no hooks here, just returns the component
  const renderRangeFields = (title, minField, maxField, options) => {
    return (
      <RangeFieldComponent
        title={title}
        minField={minField}
        maxField={maxField}
        options={options}
      />
    );
  };

  return (
    <div className={`${classes.searchSidebar} ${isHorizontal ? classes.horizontalLayout : classes.verticalLayout}`}>
      {!showAdvanced ? (
        <>
          {conversationHistory.length > 0 && (
            <div 
              ref={conversationRef}
              className={classes.conversationHistory}
            >
              {conversationHistory.map((item, index) => (
                <div 
                  key={index} 
                  className={`${classes.conversationItem} ${item.type}`}
                >
                  <Typography variant="body2">
                    <strong>{item.type === 'ai' ? 'Paddy: ' : 'You: '}</strong>
                    {item.message}
                  </Typography>
                </div>
              ))}
              {isLoading && (
                <div className={classes.loadingIndicator}>
                  <Typography variant="body2">
                    <strong>Paddy:</strong> <span className="dots"></span>
                  </Typography>
                </div>
              )}
            </div>
          )}
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={5}
            label={intl.formatMessage({ id: 'shared.search' })}
            value={searchInputValue || ''}
            onChange={(e) => {
              if (typeof setSearchInputValue === 'function') {
                setSearchInputValue(e.target.value);
                console.log('Search input changed:', e.target.value); // Debug logging
              }
            }}
            onKeyPress={handleKeyPress}
            margin="normal"
            className={classes.searchBox}
            InputLabelProps={{
              color: 'primary', 
            }}
            InputProps={{
              color: 'primary',
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearchButtonClick} aria-label="search">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            fullWidth
            variant="contained"
            color='primary'
            onClick={() => {
              resetConversation();
              handleResetFilters();
            }}
            className={classes.toggleButton}
          >
            {intl.formatMessage({ id: 'search_sidebar.reset' })}
          </Button>
          <Button
            fullWidth
            color='secondary'
            variant="contained"
            onClick={toggleAdvanced}
            className={classes.toggleButton}
          >
            {intl.formatMessage({ id: 'search_sidebar.advanced_search' })}
          </Button>
        </>
      ) : (
        isHorizontal ? (
          // Horizontal Layout
          <>
            {/* First row of filters */}
            <Grid container spacing={2} className={classes.filterSection}>
              <Grid item xs={12} sm={6} md={2}>
                {renderTextField("Practice ID", "name")}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("Interested in", "transitionGoalsName", (transitionGoals || []).map(name => ({ id: name, name })), true)}
              </Grid>
              {data?.company?.business_type === "wealth_rj" && <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("Planning Preference", "planningPreference", [
                  { id: "find_partner", name: "Find Partner" },
                  { id: "become_partner", name: "Become Partner" },
                  { id: "already_have", name: "Already Have" },
                  { id: "no_plan_preference", name: "No Preference" }
                ])}
              </Grid>}
              <Grid item xs={12} sm={6} md={2}>
                {renderTextField("mi from me", "locationWithin", "number")}
              </Grid>
              {featureEnabled(data, "search_amp_trainee") && <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("AMP Trainee Status", "ampStatus", [
                  { id: "yes", name: "Yes" },
                  { id: "no", name: "No" }
                ])}
              </Grid>}
              <Grid item xs={12} sm={6} md={2}>
                <Box display="flex" alignItems="flex-end" height="100%">
                  <Button 
                    fullWidth
                    variant="contained" 
                    className={classes.resetButton}
                    onClick={handleResetFilters}
                  >
                    {intl.formatMessage({ id: 'search_sidebar.reset' })}
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Second row of filters */}
            <Grid container spacing={2} className={classes.filterSection}>
              <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("Province/State", "province", regionForCountry(data?.company?.country) || [])}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("Designation", "designations", data?.availableDesignations || [], true)}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("Affiliations", "affiliations", data?.availableAffiliations || [], true)}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("Languages", "languages", data?.company?.languages || [], true)}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("Primary Markets", "primaryMarkets", (data?.company?.available_primary_markets || []).map(value => ({
                  id: value,
                  name: value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                })), true)}
              </Grid>
            </Grid>

            {/* Third row of filters */}
            <Grid container spacing={2} className={classes.filterSection}>
              {featureEnabled(data, 'enabled_student_profiles') && <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("Student Status", "isStudent", [
                  { id: true, name: "Yes" },
                  { id: false, name: "No" }
                ])}
              </Grid>}
              {featureEnabled(data, "partial_sales") && <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("Has Partial Sale Listing", "hasPartialSales", [
                  { id: true, name: "Yes" },
                  { id: false, name: "No" }
                ], false, "Select...")}
              </Grid>}
              {featureEnabled(data, "partial_sales") && localSearchParams.hasPartialSales === true && <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth variant={isHorizontal ? "outlined" : "standard"} size={isHorizontal ? "small" : "medium"} margin={isHorizontal ? "none" : "normal"}>
                  <Typography className={classes.inputLabel}>
                    Partial Sale Locations
                  </Typography>
                  <Select
                    multiple
                                            value={localSearchParams.partialSaleLocations || []}
                        onChange={(event) => {
                          handleLocalSearchChange('partialSaleLocations', event.target.value);
                        }}
                    renderValue={(selected) => selected.length > 0 ? `${selected.length} selected` : 'Select locations'}
                    displayEmpty
                  >
                    {regionForCountry(data?.company?.country || 'us').map((location) => (
                      <MenuItem key={location} value={location}>
                        <Checkbox checked={(localSearchParams.partialSaleLocations || []).indexOf(location) > -1} />
                        {location}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>}
              <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("AMP Trainee Status", "ampTraineeStatus", [
                  { id: true, name: "Yes" },
                  { id: false, name: "No" }
                ])}
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("Discretionary Investment Models", "discretionaryInvestmentModels", [
                  { id: true, name: "Yes" },
                  { id: false, name: "No" }
                ])}
              </Grid>
              {featureEnabled(data, 'is_limited_user_filter') && <Grid item xs={12} sm={6} md={2}>
                {renderSelectField("Is Limited User", "isLimitedUser", [
                  { id: true, name: "Yes" },
                  { id: false, name: "No" }
                ])}
              </Grid>}
            </Grid>

            {/* Range sections */}
            {data?.company?.business_type === "wealth_rj" ? <Grid container spacing={4} className={classes.filterSection}>
              <Grid item xs={12} md={3}>
                {renderRangeFields("Gross Production", "grossProductionGt", "grossProductionLt", {
                  min: [
                    { value: "100000", label: "100,000" },
                    { value: "250000", label: "250,000" },
                    { value: "500000", label: "500,000" }
                  ],
                  max: [
                    { value: "500000", label: "500,000" },
                    { value: "1000000", label: "1,000,000" },
                    { value: "2000000", label: "2,000,000" }
                  ]
                })}
              </Grid>
              <Grid item xs={12} md={3}>
                {renderRangeFields("Assets Under Management", "assetsUnderManagementGt", "assetsUnderManagementLt", {
                  min: [
                    { value: "1000000", label: "1M" },
                    { value: "5000000", label: "5M" },
                    { value: "10000000", label: "10M" }
                  ],
                  max: [
                    { value: "10000000", label: "10M" },
                    { value: "50000000", label: "50M" },
                    { value: "100000000", label: "100M" }
                  ]
                })}
              </Grid>
              <Grid item xs={12} md={3}>
                {renderRangeFields("Years In Practice", "yearsInPracticeGt", "yearsInPracticeLt", {
                  min: [
                    { value: "1", label: "1" },
                    { value: "5", label: "5" },
                    { value: "10", label: "10" }
                  ],
                  max: [
                    { value: "10", label: "10" },
                    { value: "20", label: "20" },
                    { value: "30", label: "30" }
                  ]
                })}
              </Grid>
              <Grid item xs={12} md={3}>
                {renderRangeFields("Number Of Clients", "numberOfClientsGt", "numberOfClientsLt", {
                  min: [
                    { value: "50", label: "50" },
                    { value: "100", label: "100" },
                    { value: "200", label: "200" }
                  ],
                  max: [
                    { value: "200", label: "200" },
                    { value: "500", label: "500" },
                    { value: "1000", label: "1000" }
                  ]
                })}
              </Grid>
            </Grid> 
            :
              <Grid container spacing={2} className={classes.filterSection}>
                <Grid item xs={12} md={3}>
                  {renderRangeFields("Last 12-Month Revenue", "annualRevenueGt", "annualRevenueLt", {
                    min: [
                      { value: "100000", label: "$100,000" },
                      { value: "250000", label: "$250,000" },
                      { value: "500000", label: "$500,000" },
                      { value: "1000000", label: "$1,000,000" },
                      { value: "2000000", label: "$2,000,000" },
                      { value: "3000000", label: "$3,000,000" },
                      { value: "5000000", label: "$5,000,000" }
                    ],
                    max: [
                      { value: "250000", label: "$250,000" },
                      { value: "500000", label: "$500,000" },
                      { value: "1000000", label: "$1,000,000" },
                      { value: "2000000", label: "$2,000,000" },
                      { value: "3000000", label: "$3,000,000" },
                      { value: "5000000", label: "$5,000,000" },
                      { value: "10000000", label: "$10,000,000" }
                    ]
                  })}
                </Grid>
              </Grid>
            }

            {/* Bottom row with keywords and search */}
            <Grid container spacing={2} className={classes.filterSection}>
              <Grid item xs={12} sm={3}>
              <Typography variant="subtitle2" gutterBottom style={{marginTop: 16}}>
                    {intl.formatMessage({ id: 'search_sidebar.keywords' })}
                  </Typography>
                  {renderSelectField(
                    "Keywords",
                    "tags",
                    (data?.availableStrengths || []).map(strength => ({
                      id: strength,
                      name: strength.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
                    })),
                    true,
                    intl.formatMessage({ id: 'search_sidebar.keywords_placeholder' })
                  )}
              </Grid>
              <Grid item xs={12} sm={2}>
                <Box display="flex" alignItems="flex-end" height="100%">
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    className={classes.searchButton}
                    onClick={handleSearchButtonClick}
                  >
                    {intl.formatMessage({ id: 'search_sidebar.search_now' })}
                  </Button>
                </Box>
              </Grid>
            </Grid>
            {allowAISearch && <Button
              variant="outlined"
              onClick={toggleAdvanced}
              size="small"
              style={{ color: 'white', borderColor: 'white' }}
            >
              {intl.formatMessage({ id: 'search_sidebar.back_to_simple_search' })}
            </Button>}
          </>
        ) : (
          // Vertical Layout Advanced Search (updated to match horizontal fields)
          <>
            {allowAISearch && <>
              <div className={classes.advancedSearchHeader}>
                <Typography variant="h6">
                  {intl.formatMessage({ id: 'search_sidebar.advanced_search' })}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={toggleAdvanced}
                  size="small"
                >
                  {intl.formatMessage({ id: 'search_sidebar.back_to_simple_search' })}
                </Button>
              </div>
            </>}

            <div className={classes.filterRow}>
              <div className={classes.filterGrid}>
                {/* Column 1: Basic Info */}
                <div>
                  <Typography variant="subtitle1" gutterBottom>
                    {intl.formatMessage({ id: 'search_sidebar.basic_info' })}
                  </Typography>
                  {renderTextField("Practice ID", "name")}
                  {renderSelectField("Interested in", "transitionGoalsName", (transitionGoals || []).map(name => ({ id: name, name })), true)}
                  {data?.company?.business_type === "wealth_rj" && renderSelectField("Planning Preference", "planningPreference", [
                    { id: "find_partner", name: "Find Partner" },
                    { id: "become_partner", name: "Become Partner" },
                    { id: "already_have", name: "Already Have" },
                    { id: "no_plan_preference", name: "No Preference" }
                  ])}
                  {featureEnabled(data, "search_map_trainee") && renderSelectField("AMP Trainee Status", "ampTraineeStatus", [
                    { id: true, name: "Yes" },
                    { id: false, name: "No" }
                  ])}
                  {renderSelectField("Student Status", "isStudent", [
                    { id: true, name: "Yes" },
                    { id: false, name: "No" }
                  ])}
                  {renderSelectField("Discretionary Investment Models", "discretionaryInvestmentModels", [
                    { id: true, name: "Yes" },
                    { id: false, name: "No" }
                  ])}
                  {featureEnabled(data, 'is_limited_user_filter') && renderSelectField("Is Limited User", "isLimitedUser", [
                    { id: true, name: "Yes" },
                    { id: false, name: "No" }
                  ])}
                </div>

                {/* Column 2: Location & Languages */}
                <div>
                  <Typography variant="subtitle1" gutterBottom>
                    {intl.formatMessage({ id: 'search_sidebar.distance' })}
                  </Typography>
                  {renderTextField("Miles from me", "locationWithin", "number")}
                  {renderSelectField("Province/State", "province", regionForCountry(data?.company?.country) || [])}
                  {renderSelectField("Languages", "languages", data?.company?.languages || [], true)}
                  {renderSelectField("Primary Markets", "primaryMarkets", (data?.company?.available_primary_markets || []).map(value => ({
                    id: value,
                    name: value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                  })), true)}
                </div>

                {/* Column 3: Business Metrics */}
                <div>
                  <Typography variant="subtitle1" gutterBottom>
                    {intl.formatMessage({ id: 'search_sidebar.business_metrics' })}
                  </Typography>
                  {featureEnabled(data, "partial_sales") && renderSelectField("Has Partial Sale Listing", "hasPartialSales", [
                    { id: true, name: "Yes" },
                    { id: false, name: "No" }
                  ], false, "Select...")}
                  {featureEnabled(data, "partial_sales") && localSearchParams.hasPartialSales === true && (
                    <FormControl fullWidth variant={isHorizontal ? "outlined" : "standard"} size={isHorizontal ? "small" : "medium"} margin={isHorizontal ? "none" : "normal"}>
                      <Typography className={classes.inputLabel}>
                        Partial Sale Locations
                      </Typography>
                      <Select
                        multiple
                        value={localSearchParams.partialSaleLocations || []}
                        onChange={(event) => {
                          handleLocalSearchChange('partialSaleLocations', event.target.value);
                        }}
                        renderValue={(selected) => selected.length > 0 ? `${selected.length} selected` : 'Select locations'}
                        displayEmpty
                      >
                        {regionForCountry(data?.company?.country || 'us').map((location) => (
                          <MenuItem key={location} value={location}>
                            <Checkbox checked={(localSearchParams.partialSaleLocations || []).indexOf(location) > -1} />
                            {location}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {data?.company?.business_type !== "wealth_rj" ? (
                    <>
                      <Typography variant="subtitle2" gutterBottom style={{marginTop: 16}}>
                        {intl.formatMessage({ id: 'shared.last_twelve_month' })}
                      </Typography>
                      {renderTextField("Min Annual Revenue", "annualRevenueGt", "number", "$100,000")}
                      {renderTextField("Max Annual Revenue", "annualRevenueLt", "number", "$10,000,000")}
                    </>
                  ) : (
                    <>

                      <Typography variant="subtitle2" gutterBottom style={{marginTop: 16}}>
                        {intl.formatMessage({ id: 'search_sidebar.gross_production' })}
                      </Typography>
                      {renderTextField("Min Revenue", "grossProductionGt", "number")}
                      {renderTextField("Max Revenue", "grossProductionLt", "number")}
                      
                      <Typography variant="subtitle2" gutterBottom style={{marginTop: 16}}>
                        {intl.formatMessage({ id: 'search_sidebar.assets_under_management' })}
                      </Typography>
                      {renderTextField("Min AUM", "assetsUnderManagementGt", "number")}
                      {renderTextField("Max AUM", "assetsUnderManagementLt", "number")}
                      
                      <Typography variant="subtitle2" gutterBottom style={{marginTop: 16}}>
                        {intl.formatMessage({ id: 'search_sidebar.practice_information' })}
                      </Typography>
                      {renderTextField("Min Years in Practice", "yearsInPracticeGt", "number")}
                      {renderTextField("Max Years in Practice", "yearsInPracticeLt", "number")}
                      {renderTextField("Min Clients", "numberOfClientsGt", "number")}
                      {renderTextField("Max Clients", "numberOfClientsLt", "number")}
                    </>
                  )}
                </div>

                {/* Column 4: Qualifications */}
                <div>
                  <Typography variant="subtitle1" gutterBottom>
                    {intl.formatMessage({ id: 'search_sidebar.qualifications' })}
                  </Typography>
                  {renderSelectField("Designations", "designations", data?.availableDesignations || [], true)}
                  {renderSelectField("Affiliations", "affiliations", data?.availableAffiliations || [], true)}
                  
                  <Typography variant="subtitle2" gutterBottom style={{marginTop: 16}}>
                    {intl.formatMessage({ id: 'search_sidebar.keywords' })}
                  </Typography>
                  {renderSelectField(
                    "Keywords",
                    "tags",
                    (data?.availableStrengths || []).map(strength => ({
                      id: strength,
                      name: strength.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
                    })),
                    true,
                    intl.formatMessage({ id: 'search_sidebar.keywords_placeholder' })
                  )}
                </div>
              </div>
            </div>

            <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '16px'}}>
              <Button 
                variant="contained" 
                color=""
                onClick={handleResetFilters}
              >
                {intl.formatMessage({ id: 'search_sidebar.reset_filters' })}
              </Button>
              
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleSearchButtonClick}
              >
                {intl.formatMessage({ id: 'search_sidebar.search_now' })}
              </Button>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default injectIntl(SearchSidebar);